<script setup lang="ts">
import bottlePlaceholder from '@/assets/images/wine_placeholder.png'
import { useViewport } from '@/composables/useViewport'
import { useUiStore } from '@/stores/ui'
import { useWineStore } from '@/stores/wine'
import { ScannedWine } from '@/types'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'

const route = useRoute()
// const router = useRouter()
// const authStore = useAuthStore()

// Viewport
useViewport()

// Props
const scanId = computed(() => route.params.id as string)
const wineStore = useWineStore()

// State
const scanData = ref<ScannedWine | null>(null) // Все данные скана
const isLoading = ref(false)
const error = ref<string | null>(null)
const wineName = ref<HTMLDivElement | null>(null)
const wineInfo = ref<HTMLDivElement | null>(null)
const wineInfoContent = ref<HTMLDivElement | null>(null)
const wineBtnFixedWrap = ref<HTMLDivElement | null>(null)
const wineNameHeight = ref(0)
const isInfoScrolledToTop = ref(false)
const shouldHighlightButton = ref(false)
let nameResizeObserver: ResizeObserver | null = null
let windowResizeHandler: (() => void) | null = null

const bottleImageUrl = computed(() => scanData.value?.wine?.bottle_image?.url)
const topBackgroundStyle = computed(() =>
    scanData.value?.wine?.bg_image
        ? {
              backgroundImage: `url(${scanData.value.wine.bg_image.url})`,
              backgroundPosition: 'center',
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
          }
        : {}
)

// Computed properties
// const wineDishes = computed(() => scanData.value?.wine?.dishes || [])

const defaultDescription = 'Описание будет добавлено позже.'

// Simple HTML sanitization for description
const sanitizeDescription = (html: string | undefined | null): string => {
    if (!html) return defaultDescription

    // Remove potentially dangerous tags and attributes
    return html
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/on\w+="[^"]*"/gi, '')
        .replace(/javascript:/gi, '')
        .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
        .replace(/<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi, '')
        .replace(/<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi, '')
}

const safeDescription = computed(() => sanitizeDescription(scanData.value?.wine?.description))

const handleInfoScroll = () => {
    if (!wineInfoContent.value || !wineName.value) return

    // Обновляем viewport перед проверкой для корректной работы в Telegram Web App
    // Это важно, так как viewport может изменяться при скролле из-за адресной строки
    const updateViewport = () => {
        const height = window.visualViewport?.height || window.innerHeight
        const vh = height * 0.01
        document.documentElement.style.setProperty('--vh', `${vh}px`)
    }
    updateViewport()

    const infoRect = wineInfoContent.value.getBoundingClientRect()
    const nameRect = wineName.value.getBoundingClientRect()

    // Скролл разблокирован только когда доскроллили до нижней границы wineName
    // Добавляем небольшой threshold (2px) для более надежной работы в Telegram Web App
    const threshold = 2
    const shouldUnlock = nameRect.bottom + threshold >= infoRect.top
    isInfoScrolledToTop.value = shouldUnlock

    // Проверяем, достигла ли верхняя граница wine-detail__info верхней границы кнопки
    if (wineInfo.value && wineBtnFixedWrap.value) {
        const infoRect = wineInfo.value.getBoundingClientRect()
        const btnRect = wineBtnFixedWrap.value.getBoundingClientRect()

        // Кнопка подсвечивается когда верхняя граница info достигла верхней границы кнопки + 20px
        const shouldHighlight = infoRect.top + 20 <= btnRect.top
        shouldHighlightButton.value = shouldHighlight
    }
}

// Lifecycle
onMounted(async () => {
    try {
        isLoading.value = true
        error.value = null
        const ui = useUiStore()
        ui.beginDataLoad()
        const result = await wineStore.loadScan(scanId.value)
        if (result.success && result.data) {
            scanData.value = result.data
        }
    } catch (e: unknown) {
        error.value = (e as Error)?.message || 'Ошибка загрузки вина'
    } finally {
        isLoading.value = false
        const ui = useUiStore()
        ui.endDataLoad()
    }

    const updateNameHeight = () => {
        wineNameHeight.value = wineName.value?.clientHeight || 0
    }

    updateNameHeight()

    if ('ResizeObserver' in window && wineName.value) {
        nameResizeObserver = new ResizeObserver(() => {
            // Добавляем debounce для улучшения производительности
            requestAnimationFrame(updateNameHeight)
        })
        nameResizeObserver.observe(wineName.value)
    } else {
        // Fallback: обновление по ресайзу окна с passive listener
        windowResizeHandler = () => requestAnimationFrame(updateNameHeight)
        window.addEventListener('resize', windowResizeHandler, { passive: true })
    }

    // Add scroll listeners
    if (wineInfoContent.value) {
        wineInfoContent.value.addEventListener('scroll', handleInfoScroll, { passive: true })
    }
    window.addEventListener('scroll', handleInfoScroll, { passive: true })

    // Проверяем начальное состояние
    handleInfoScroll()
})

onBeforeUnmount(() => {
    if (nameResizeObserver) {
        nameResizeObserver.disconnect()
        nameResizeObserver = null
    }
    if (windowResizeHandler) {
        window.removeEventListener('resize', windowResizeHandler)
        windowResizeHandler = null
    }
    if (wineInfoContent.value) {
        wineInfoContent.value.removeEventListener('scroll', handleInfoScroll)
    }
    window.removeEventListener('scroll', handleInfoScroll)
})
</script>

<template>
    <div class="wine-detail">
        <div class="wine-detail__top" :style="topBackgroundStyle">
            <div class="wine-detail__name" ref="wineName">
                {{ scanData?.wine?.name }},
                <template v-if="scanData?.wine?.year"> {{ scanData.wine.year }}</template>
            </div>
            <div class="wine-detail__bottle-wrap">
                <div class="wine-detail__circulation">
                    <div>Item</div>
                    <div class="wine-detail__circulation-bold">
                        №{{ scanData?.bottle?.serial_no ?? '—' }}
                    </div>
                    <div>out of {{ scanData?.bottle_total ?? 10000 }}</div>
                </div>
                <img
                    :src="bottleImageUrl || bottlePlaceholder"
                    alt=""
                    class="wine-detail__bottle"
                />
            </div>
        </div>
        <div
            ref="wineInfo"
            class="wine-detail__info"
            :class="{ 'wine-detail__info--scroll-locked': !isInfoScrolledToTop }"
            :style="{
                'max-height': `calc(var(--vh, 1vh) * 100 - var(--app-footer-height) - ${wineNameHeight}px)`,
            }"
        >
            <div
                ref="wineInfoContent"
                class="wine-detail__info-content"
                :style="{
                    'max-height': `calc(var(--vh, 1vh) * 100 - var(--app-footer-height) - ${wineNameHeight}px)`,
                }"
            >
                <!-- <div class="wine-detail__taste">
                    <div class="taste taste_dark">
                        <div class="taste__item" v-if="wineDishes.length > 0">
                            <div class="taste__title">Гастрономия</div>
                            <div class="taste__food-icon-wrap">
                                <div v-for="dish in wineDishes" class="taste__food-icon">
                                    <img
                                        :src="dish.image?.url"
                                        :alt="dish.image?.alt || dish.name"
                                    />
                                </div>
                            </div>
                        </div>
                        <div class="taste__border" v-if="wineDishes.length > 0"></div>
                        <div
                            class="taste__item"
                            v-if="
                                scanData?.wine?.serving_temp_min != null ||
                                scanData?.wine?.serving_temp_max != null
                            "
                        >
                            <div class="taste__title">Подача</div>
                            <div
                                class="taste__temp"
                                v-if="
                                    scanData?.wine?.serving_temp_min != null &&
                                    scanData?.wine?.serving_temp_max != null
                                "
                            >
                                {{ Math.round(scanData.wine.serving_temp_min) }}-{{
                                    Math.round(scanData.wine.serving_temp_max)
                                }}°C
                            </div>
                            <div
                                class="taste__temp"
                                v-else-if="scanData?.wine?.serving_temp_min != null"
                            >
                                от {{ Math.round(scanData.wine.serving_temp_min) }}°C
                            </div>
                            <div
                                class="taste__temp"
                                v-else-if="scanData?.wine?.serving_temp_max != null"
                            >
                                до {{ Math.round(scanData.wine.serving_temp_max) }}°C
                            </div>
                            <div class="taste__temp" v-else>—</div>
                        </div>
                    </div>
                </div> -->

                <div class="wine-detail__text-wrap">
                    <div class="wine-detail__desc" v-html="safeDescription"></div>
                </div>
                <div class="wine-detail__actions">
                    <a
                        v-if="scanData?.wine?.winemaker?.website"
                        :href="scanData?.wine?.winemaker?.website"
                        class="btn btn_empty"
                        >Страница винодела</a
                    >
                    <div
                        ref="wineBtnFixedWrap"
                        class="wine-detail__btn-fixed-wrap"
                        :class="{
                            'wine-detail__btn-fixed-wrap--highlighted': shouldHighlightButton,
                        }"
                    >
                        <RouterLink
                            :to="{
                                name: 'wine-review',
                                params: { id: scanData?.id },
                                query: scanData?.review ? { readOnly: 'true' } : {},
                            }"
                            class="wine-detail__btn-fixed btn"
                        >
                            {{ scanData?.review ? 'Show review' : 'Leave feedback' }}
                        </RouterLink>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="stylus">
.taste
    display flex
    align-items stretch
    gap 16px
    padding 10px 12px
    background-color grey
    border-radius 16px
    &_dark
        background-color transparent
        padding 8px 0 0
    &__border
        width 1px
        // height 100%
        background-color white
        ^[0]_dark &
            opacity 0.2
            background-color var(--app-text-color)
    &__title
        text-align center
        font-size 16px
        line-height 19px
        font-weight 600
        color var(--app-text-light-color)
        ^[0]_dark &
            text-align left
            color var(--app-text-color)
    &__food-icon-wrap
        display flex
        gap 8px
    &__food-icon
        width 32px
        height 32px
        ^[0]_dark &
            width 37px
            height 37px
            background var(--app-black)
            border-radius 50%
        & img
            width 100%
            height 100%
            object-fit contain
    &__item
        display flex
        flex-direction column
        gap 12px
    &__temp
        background-color var(--app-bg-color)
        padding 9px 12px 7px
        border-radius 7px
        color var(--app-text-color)
        font-size 14px
        line-height 17px
        font-weight 500
        ^[0]_dark &
            padding 12px 12px 10px
            border-radius 8px
            background-color var(--app-black)
            color var(--app-text-light-color)
            font-size 13px
            line-height 13px

.wine-detail
    &__btn-fixed-wrap
        position fixed
        z-index 20
        left 0
        bottom var(--app-footer-height)
        width 100%
        padding 8px 16px 20px
        background transparent
        transition background-color 0.3s ease

        &--highlighted
            background var(--app-bg-color)

    &__btn-fixed
        width 100%
    // &__text-wrap
    //     display flex
    //     flex-direction column
    //     gap 12px
    //     padding-bottom 24px
    &__title
        font-size 20px
        line-height 26px
        font-weight 600
        color var(--app-text-color)
    &__desc
        font-size 16px
        line-height 24px
        font-weight 400
        color var(--app-text-color)
    &__actions
        margin-top 32px
        padding 0 0 84px
        z-index 10
        display flex
        flex-direction column
        gap 12px
        background var(--app-bg-color)
    &__info
        z-index 10
        position sticky
        top 90px
        background var(--app-bg-color)
        border-radius 16px 16px 0 0
        margin-top -45px
        padding 0

        &--scroll-locked
            overflow hidden
        &:before
            content ''
            position absolute
            left 0
            top 0
            width 100%
            height 20px
            background var(--app-bg-color)
            border-radius 16px 16px 0 0
            z-index 10

    &__info-content
        width 100%
        height 100%
        padding 32px 16px 0
        overflow auto

        .wine-detail__info--scroll-locked &
            overflow hidden
    &__top
        position sticky
        top 0
        z-index 1
        background rgba(153, 31, 49, .7)
        // height calc(var(--vh, 1vh) * 100 - var(--app-footer-height))
        padding-bottom 15%
    &__winemaker-name
        font-weight 600
    &__name
        max-width 360px
        margin 0 auto
        padding 24px 16px 16px
        font-size 28px
        line-height 33px
        font-weight 400
        color var(--app-text-light-color)
        text-align center
    &__bottle
        width 100%
        // height 100%
        // margin-top 25%
        object-fit contain
        &-wrap
            position relative
            width 100%
            margin-top 20%
            text-align center
    &__circulation
        position absolute
        right 0px
        flex-shrink 0
        width 100px
        height 100px
        display flex
        flex-direction column
        justify-content center
        align-items center
        gap 3px
        // margin-top 10%
        margin-right 8%
        background-color var(--app-black)
        border-radius 50%
        padding 10px 5px 5px
        font-size 13px
        line-height 16px
        font-weight 400
        color var(--app-text-light-color)
        text-align center
        &-bold
            font-weight 600
            font-size 18px
            line-height 18px
    &__text-wrap
        display flex
        flex-direction column
        gap 16px
        padding 20px 0 0
    &__title
        font-size 20px
        line-height 26px
        font-weight 600
        color var(--app-text-color)
    &__desc
        font-size 16px
        line-height 22px
        font-weight 400
        color var(--app-text-color-secondary)
</style>

<!-- Global styles for HTML content in wine description -->
<style lang="stylus">
.wine-detail__desc
    // Styles for HTML content in description
    img
        display block
        width 100%
        height auto
        margin 12px 0
        border-radius 12px
    p, div
        margin 0 0 24px 0
        font-size 16px
        line-height 22px
        font-weight 400
        color var(--app-text-color-secondary)
        &:last-child
            margin-bottom 0
    h1, h2, h3, h4, h5, h6
        margin 0 0 12px 0
        font-weight 600
        color var(--app-text-color)
    h1
        font-size 22px
        line-height 28px
    h2
        font-size 20px
        line-height 26px
    h3
        font-size 18px
        line-height 24px
    ul, ol
        margin 12px 0
        padding-left 24px
    li
        margin 6px 0
    strong, b
        font-weight 600
    em, i
        font-style italic
    a
        color var(--app-main-accent-color)
        text-decoration none
        &:hover
            text-decoration underline
    blockquote
        margin 16px 0
        padding 12px 16px
        border-left 4px solid var(--app-main-accent-color)
        background rgba(0, 0, 0, 0.05)
        border-radius 4px
        font-style italic
</style>
